import type { AuthProvider } from '@/types/auth';

export interface AppConfig {
  authProvider: AuthProvider;
  azureClientId: string;
  azureAuthority: string;
  oidcClientId: string;
  oidcAuthority: string;
  oidcScope: string;
  oidcSilentRedirectUri: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  bolApiScope: string;
  appTitle?: string;
  debugMode?: boolean;
  // Grafana Faro configuration (app-level only, URL is handled by nginx proxy)
  faroAppName?: string;
  faroAppVersion?: string;
  faroEnvironment?: string;
  faroEnabled?: string;
  grafanaBaseUrl?: string;
  grafanaDatasourceUid?: string;
  grafanaOrgId?: string;
}

let config: AppConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  if (config) {
    return config;
  }

  try {
    // Try to load runtime configuration from /config.json (Docker environment)
    console.log('Attempting to load runtime configuration from /config.json...');
    const response = await fetch('/config.json');
    if (!response.ok) {
      throw new Error(`Failed to load config: ${response.statusText}`);
    }
    const runtimeConfig = await response.json() as Partial<AppConfig>;
    config = {
      authProvider: runtimeConfig.authProvider === 'oidc' ? 'oidc' : 'azure',
      azureClientId: runtimeConfig.azureClientId || '',
      azureAuthority: runtimeConfig.azureAuthority || '',
      oidcClientId: runtimeConfig.oidcClientId || '',
      oidcAuthority: runtimeConfig.oidcAuthority || '',
      oidcScope: runtimeConfig.oidcScope || 'openid profile email',
      oidcSilentRedirectUri:
        runtimeConfig.oidcSilentRedirectUri || `${globalThis.location.origin}/auth/silent-callback`,
      redirectUri: runtimeConfig.redirectUri || `${globalThis.location.origin}/auth/callback`,
      postLogoutRedirectUri: runtimeConfig.postLogoutRedirectUri || globalThis.location.origin,
      bolApiScope: runtimeConfig.bolApiScope || '',
      appTitle: runtimeConfig.appTitle || 'BOL Backoffice',
      debugMode: runtimeConfig.debugMode,
      faroAppName: runtimeConfig.faroAppName,
      faroAppVersion: runtimeConfig.faroAppVersion,
      faroEnvironment: runtimeConfig.faroEnvironment,
      faroEnabled: runtimeConfig.faroEnabled,
      grafanaBaseUrl: runtimeConfig.grafanaBaseUrl,
      grafanaDatasourceUid: runtimeConfig.grafanaDatasourceUid,
      grafanaOrgId: runtimeConfig.grafanaOrgId,
    };
    console.log('✅ Runtime configuration loaded from /config.json:', config);
    return config!;
  } catch (error) {
    console.log('⚠️ Failed to load runtime configuration, falling back to environment variables');
    console.log('Error details:', error);
    
    // Fallback to environment variables for development
    config = {
      authProvider: import.meta.env.VITE_AUTH_PROVIDER === 'oidc' ? 'oidc' : 'azure',
      azureClientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
      azureAuthority: import.meta.env.VITE_AZURE_AUTHORITY || '',
      oidcClientId: import.meta.env.VITE_OIDC_CLIENT_ID || '',
      oidcAuthority: import.meta.env.VITE_OIDC_AUTHORITY || '',
      oidcScope: import.meta.env.VITE_OIDC_SCOPE || 'openid profile email',
      oidcSilentRedirectUri:
        import.meta.env.VITE_OIDC_SILENT_REDIRECT_URI || `${globalThis.location.origin}/auth/silent-callback`,
      redirectUri: import.meta.env.VITE_REDIRECT_URI || `${globalThis.location.origin}/auth/callback`,
      postLogoutRedirectUri: import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || globalThis.location.origin,
      bolApiScope: import.meta.env.VITE_BOL_API_SCOPE || '',
      appTitle: import.meta.env.VITE_APP_TITLE || 'BOL Backoffice',
      debugMode: import.meta.env.VITE_DEBUG_MODE === 'true',
      // Grafana Faro configuration (app-level only, URL is proxied through nginx)
      faroAppName: import.meta.env.VITE_FARO_APP_NAME || 'bol-backoffice',
      faroAppVersion: import.meta.env.VITE_FARO_APP_VERSION || import.meta.env.VITE_APP_VERSION || '1.0.0',
      faroEnvironment: import.meta.env.VITE_FARO_ENVIRONMENT || import.meta.env.MODE || 'dev',
      faroEnabled: import.meta.env.VITE_FARO_ENABLED || 'false',
      grafanaBaseUrl: import.meta.env.VITE_GRAFANA_BASE_URL || '',
      grafanaDatasourceUid: import.meta.env.VITE_GRAFANA_DATASOURCE_UID || '',
      grafanaOrgId: import.meta.env.VITE_GRAFANA_ORG_ID || '',
    };
    
    console.log('📝 Environment variables loaded:', {
      authProvider: config.authProvider,
      azureClientId: config.azureClientId ? '***configured***' : 'MISSING',
      azureAuthority: config.azureAuthority ? '***configured***' : 'MISSING',
      oidcClientId: config.oidcClientId ? '***configured***' : 'MISSING',
      oidcAuthority: config.oidcAuthority ? '***configured***' : 'MISSING',
      oidcScope: config.oidcScope,
      oidcSilentRedirectUri: config.oidcSilentRedirectUri,
      redirectUri: config.redirectUri,
      postLogoutRedirectUri: config.postLogoutRedirectUri,
      bolApiScope: config.bolApiScope ? '***configured***' : 'MISSING',
      appTitle: config.appTitle,
      debugMode: config.debugMode,
      grafanaBaseUrl: config.grafanaBaseUrl ? '***configured***' : 'MISSING',
      grafanaDatasourceUid: config.grafanaDatasourceUid ? '***configured***' : 'MISSING',
      grafanaOrgId: config.grafanaOrgId ? '***configured***' : 'MISSING',
    });
    
    if (config.authProvider === 'azure') {
      if (!config.azureClientId) {
        throw new Error('Azure Client ID is required but not configured. Please check your .env.local file or Docker environment variables.');
      }

      if (!config.azureAuthority) {
        throw new Error('Azure Authority is required but not configured. Please check your .env.local file or Docker environment variables.');
      }

      if (!config.bolApiScope) {
        throw new Error('BOL API Scope is required but not configured. Please check your .env.local file or Docker environment variables.');
      }
    }

    if (config.authProvider === 'oidc') {
      if (!config.oidcClientId) {
        throw new Error('OIDC Client ID is required but not configured. Please check your .env.local file or Docker environment variables.');
      }

      if (!config.oidcAuthority) {
        throw new Error('OIDC Authority is required but not configured. Please check your .env.local file or Docker environment variables.');
      }

      if (!config.oidcScope) {
        throw new Error('OIDC Scope is required but not configured. Please check your .env.local file or Docker environment variables.');
      }
    }
    
    console.log('✅ Environment configuration validated successfully');
    return config;
  }
}

export function getConfig(): AppConfig {
  if (!config) {
    throw new Error('Configuration not loaded. Call loadConfig() first.');
  }
  return config;
}
